/**
 * TourContext / TourProvider — owns the route-aware product-tour engine
 * (built on driver.js).
 *
 * Surface:
 *   const { startTour } = useTour();
 *   startTour();              // manual restart (from the profile menu)
 *   startTour({ auto: true }) // first-login auto-start (records "seen")
 *
 * How it works
 * ------------
 * The tour is a multi-page journey. We drive driver.js MANUALLY (via its
 * onNextClick / onPrevClick hooks) so that, before each step renders, we can:
 *   1. navigate to the step's page (if it declares a `route`),
 *   2. wait for the step's anchor to mount and become visible,
 *   3. then highlight it.
 *
 * Robustness:
 *   - Each driver step resolves its element lazily via a function, returning
 *     the first visible selector. If nothing is found, driver shows a centered
 *     popover instead of crashing — so a missing page anchor still explains
 *     the page.
 *   - Shell-only steps (no route) whose anchor isn't visible — e.g. the
 *     desktop search box on mobile — are skipped entirely.
 *   - The tour is recorded as "seen" once, whether the user finishes, skips,
 *     or closes it (Esc / overlay / ×).
 *   - On exit we return the user to the page they started from, so the tour
 *     never loses their place.
 *
 * Mounted once inside <Layout>, so it only exists on authenticated screens.
 */
import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { driver, type Driver } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";
import { useAuth } from "@/contexts/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import type { Role } from "@/components/layouts/nav-config";
import { buildTourSteps, stepPath, type TourStep } from "./tourSteps";
import { markTourSeen } from "./tourSeen";
import { canCreateInspections } from "@/routes/role-paths";
import { api } from "@/lib/api-client";

interface StartOptions {
  /** Auto-start path: still records the tour as seen, same as a manual run. */
  auto?: boolean;
}

interface TourContextShape {
  startTour: (opts?: StartOptions) => void;
  /** True while a tour is actively running. */
  isActive: () => boolean;
}

const TourContext = createContext<TourContextShape | null>(null);

const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

// How long to poll for a step's anchor.
//   LOAD_WAIT  — used right after a route change, while the new page (and its
//                lazy chunk + data) is still painting.
//   GRACE_WAIT — used when we're ALREADY on the target page: the element is
//                either present now or genuinely absent (e.g. a permission-gated
//                button), so we only allow a short grace for late renders. This
//                keeps "Next" responsive instead of hanging for seconds before
//                skipping a step that can never appear for this role/account.
const LOAD_WAIT = 4000;
const GRACE_WAIT = 1200;

/** Does the user have at least one record at `path`? Used to decide whether to
 *  include (and count) the deep-dive steps that open a record. On any error
 *  (offline / network) we return true so the steps are kept optimistically. */
async function hasAnyRecords(
  path: string,
  query?: Record<string, string | number>,
): Promise<boolean> {
  try {
    const r = await api.paged<unknown>(path, { limit: 1, ...query });
    return (r.meta?.total ?? r.data?.length ?? 0) > 0;
  } catch {
    return true;
  }
}

function selectorsOf(step: TourStep): string[] {
  if (!step.element) return [];
  return Array.isArray(step.element) ? step.element : [step.element];
}

/** An element counts only if rendered AND visible (not display:none). */
function isVisible(el: Element): boolean {
  const he = el as HTMLElement;
  return he.offsetParent !== null || he.getClientRects().length > 0;
}

/** First visible element among a step's selectors, or null. */
function resolveElement(step: TourStep): HTMLElement | null {
  for (const s of selectorsOf(step)) {
    const el = document.querySelector(s);
    if (el && isVisible(el)) return el as HTMLElement;
  }
  return null;
}

/** Poll until one of the selectors is visible, or the timeout elapses. */
async function waitForElement(step: TourStep, timeoutMs: number): Promise<HTMLElement | null> {
  const deadline = Date.now() + timeoutMs;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const el = resolveElement(step);
    if (el) return el;
    if (Date.now() >= deadline) return null;
    await delay(80);
  }
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { canViewFeature } = usePermission();
  const navigate = useNavigate();
  const driverRef = useRef<Driver | null>(null);

  // The async step machinery is registered once but must always see the latest
  // navigate fn / user — keep them in refs.
  const navRef = useRef(navigate);
  const userRef = useRef(user);
  // Set just before an unmount-driven teardown so onDestroyed knows NOT to
  // navigate the user back (it would fight the logout/route change that caused
  // the unmount).
  const unmountingRef = useRef(false);
  // Bumped on each startTour() call so an in-flight async pre-check from an
  // earlier call can detect it was superseded and bail.
  const runTokenRef = useRef(0);
  // canViewFeature changes identity each render; ref keeps startTour (registered
  // once) reading the latest RBAC gate.
  const canViewFeatureRef = useRef(canViewFeature);
  useEffect(() => { navRef.current = navigate; }, [navigate]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { canViewFeatureRef.current = canViewFeature; }, [canViewFeature]);

  // Tidy up if the provider unmounts mid-tour (e.g. logout).
  useEffect(() => () => { unmountingRef.current = true; driverRef.current?.destroy(); }, []);

  const startTour = useCallback((_opts?: StartOptions) => {
    driverRef.current?.destroy();

    const myToken = ++runTokenRef.current;
    const role = (userRef.current?.role ?? null) as Role | null;
    const u = userRef.current;
    // Can this user start/import an inspection? Used to pre-filter the
    // "Start or import an inspection" step so the step COUNT is accurate.
    // (The step is also requireElement, so it still self-skips at runtime.)
    const canStartInspection =
      canCreateInspections(u?.role, u?.rank) || u?.role === "admin";

    // The deep-dive steps can only OPEN a record that exists. Check once up
    // front whether the user has any inspections / observations so those steps
    // (and their count) are included only when there's something to open —
    // otherwise the "Step X of Y" total would be too high for empty accounts.
    const candidates = buildTourSteps(role, canViewFeatureRef.current);
    const wantsInspections = candidates.some(
      (s) => s.open === "inspection" || s.open === "inspection-questions");
    const wantsObservations = candidates.some((s) => s.open === "observation");
    const wantsPiq = candidates.some(
      (s) => s.open === "piq" || s.open === "piq-questions");

    void Promise.all([
      wantsInspections ? hasAnyRecords("/api/inspections") : Promise.resolve(false),
      wantsObservations ? hasAnyRecords("/api/negative-observations") : Promise.resolve(false),
      // PIQ is excluded from the unfiltered inspections list, so ask explicitly.
      wantsPiq ? hasAnyRecords("/api/inspections", { type: "piq" }) : Promise.resolve(false),
    ]).then(([hasInspections, hasObservations, hasPiq]) => {
      // Bail if a newer startTour() superseded this run while we awaited.
      if (myToken !== runTokenRef.current) return;

    // Drop steps that won't appear for this user/viewport so the "Step X of Y"
    // total is right up front: search when hidden, the create/import action
    // without permission, and the record deep-dives when there are no records.
    const steps = candidates.filter((step) => {
      // Shell steps whose anchor can be absent (hidden on mobile / role-gated):
      // include (and count) them only when the anchor is actually present now,
      // so the "Step X of Y" total is right up front.
      if (step.id === "search") return resolveElement(step) != null;
      if (!step.route && !step.open && step.requireElement) return resolveElement(step) != null;
      if (step.id === "inspections-actions" || step.id === "piq-actions") return canStartInspection;
      if (step.open === "inspection" || step.open === "inspection-questions") return hasInspections;
      if (step.open === "observation") return hasObservations;
      if (step.open === "piq" || step.open === "piq-questions") return hasPiq;
      return true;
    });
    if (steps.length === 0) {
      markTourSeen(userRef.current);
      return;
    }

    const total = steps.length;
    let shown = 0; // running ordinal of the step currently on screen (1-based)

    const startPath = window.location.pathname;

    // Build driver steps. element resolves lazily at render time (after we've
    // navigated + waited), returning the live node or undefined → driver shows
    // a centered popover instead of crashing on a missing anchor.
    const driveSteps = steps.map((step) => ({
      element: step.element
        ? (() => resolveElement(step) ?? undefined) as () => Element
        : undefined,
      popover: { title: step.title, description: step.description },
    }));

    let recorded = false;
    const recordSeen = () => {
      if (recorded) return;
      recorded = true;
      markTourSeen(userRef.current);
    };

    // Detail-page steps open the FIRST record of a kind. We read the row's
    // link from the relevant list once, then reuse it (memoised) so we don't
    // bounce back to the list for every sub-step on the same record.
    let inspHref: string | null | undefined;
    let obsHref: string | null | undefined;
    let piqHref: string | null | undefined;

    /** Ensure we're on a list page, then return its first row's href (or null). */
    const firstRowHref = async (
      listRoute: string,
      rowSelector: string,
    ): Promise<string | null> => {
      const listPath = stepPath(listRoute, role);
      const navigated = window.location.pathname !== listPath;
      if (navigated) {
        navRef.current(listPath);
        await delay(350);
      }
      const deadline = Date.now() + (navigated ? LOAD_WAIT : GRACE_WAIT);
      while (Date.now() < deadline) {
        const el = document.querySelector(rowSelector) as HTMLElement | null;
        const href = el?.getAttribute("href");
        if (href) return href;
        await delay(80);
      }
      return null;
    };

    const getInspectionHref = async (): Promise<string | null> => {
      if (inspHref === undefined) {
        inspHref = await firstRowHref("/manage-inspections", '[data-tour="inspection-row"]');
      }
      return inspHref;
    };
    const getObservationHref = async (): Promise<string | null> => {
      if (obsHref === undefined) {
        obsHref = await firstRowHref("/observations", '[data-tour="observation-row"]');
      }
      return obsHref;
    };
    const getPiqHref = async (): Promise<string | null> => {
      // PIQ rows live on the /pre-inspections list but reuse the same row anchor.
      if (piqHref === undefined) {
        piqHref = await firstRowHref("/pre-inspections", '[data-tour="inspection-row"]');
      }
      return piqHref;
    };

    /** Resolve + navigate to a step that opens a record's detail page. */
    const prepareOpen = async (step: TourStep): Promise<void> => {
      let target: string | null = null;
      if (step.open === "inspection") target = await getInspectionHref();
      else if (step.open === "inspection-questions") {
        const h = await getInspectionHref();
        target = h ? `${h}/questions` : null;
      } else if (step.open === "observation") target = await getObservationHref();
      else if (step.open === "piq") target = await getPiqHref();
      else if (step.open === "piq-questions") {
        const h = await getPiqHref();
        target = h ? `${h}/questions` : null;
      }
      // No record found → leave as-is; the step's anchor won't be present and
      // (being requireElement) it will be skipped by the caller.
      if (!target) return;
      const navigated = window.location.pathname !== target;
      if (navigated) {
        navRef.current(target);
        await delay(450); // detail pages are heavier — give them a beat
      }
      // Detail pages are data-heavy; allow a touch longer than a list page when
      // we actually navigated, but stay snappy when we're already there.
      if (step.element) await waitForElement(step, navigated ? LOAD_WAIT + 1500 : GRACE_WAIT);
    };

    /** Navigate to a step's page (if needed) and wait for its anchor. */
    const prepare = async (step: TourStep): Promise<void> => {
      if (step.open) {
        await prepareOpen(step);
      } else if (step.route) {
        const target = stepPath(step.route, role);
        const navigated = window.location.pathname !== target;
        if (navigated) {
          navRef.current(target);
          await delay(350); // let the route swap + lazy chunk begin painting
        }
        // Long poll only while a freshly-navigated page loads; otherwise a
        // short grace so a permission-gated / empty-state anchor that will
        // never appear is skipped quickly (keeps "Next" responsive).
        if (step.element) await waitForElement(step, navigated ? LOAD_WAIT : GRACE_WAIT);
        else if (navigated) await delay(150);
      } else if (step.element) {
        // Shell step — page is already loaded; short grace only.
        await waitForElement(step, GRACE_WAIT);
      }
    };

    let busy = false; // guard against double-advances while navigating

    /**
     * Show the first PRESENTABLE step at/after `from`, travelling in `dir`,
     * navigating and waiting as needed. A step is skipped when:
     *   - it's a shell step (no route) whose anchor isn't visible right now, or
     *   - it's flagged `requireElement` and, after navigating to its page, the
     *     anchor still isn't there (e.g. a permission-gated button).
     * Off the end (dir 1) → finish; off the start (dir -1) → stay put.
     */
    const showFrom = async (from: number, dir: 1 | -1) => {
      if (busy) return;
      busy = true;
      try {
        let i = from;
        while (i >= 0 && i < steps.length) {
          const step = steps[i];
          // Pre-nav skip: a SHELL step (no route, no record-open) whose anchor
          // isn't visible now. Route/open steps navigate inside prepare(), so
          // they must not be pre-skipped on the strength of the current page.
          if (!step.route && !step.open && step.element && !resolveElement(step)) { i += dir; continue; }
          await prepare(step);
          // Post-nav skip: gated feature whose anchor never appeared.
          if (step.requireElement && step.element && !resolveElement(step)) { i += dir; continue; }
          const d = driverRef.current;
          if (!d) return;
          if (d.isActive()) {
            // One real step shown per Next/Back → move the ordinal by exactly
            // one, regardless of how many were skipped in between (no jumps).
            shown = Math.min(total, Math.max(1, shown + dir));
            d.moveTo(i);
          } else {
            shown = 1;
            // Mark "seen" the instant the tour actually appears, so a first-time
            // user is never flagged seen without having seen it (idempotent).
            recordSeen();
            d.drive(i);
          }
          return;
        }
        if (dir === 1) driverRef.current?.destroy(); // past last → finish
        // dir === -1 past the start: do nothing, keep the current step.
      } finally {
        busy = false;
      }
    };

    const advance = (dir: 1 | -1) => {
      const d = driverRef.current;
      if (!d) return;
      void showFrom((d.getActiveIndex() ?? 0) + dir, dir);
    };

    const d = driver({
      // We render progress OURSELVES as a running "Step X of Y" (see
      // onPopoverRender): X moves one at a time so it never jumps when steps are
      // skipped at runtime. showProgress must be true so the progress element
      // exists for us to write into; the template is overwritten on every render.
      showProgress: true,
      progressText: "Step {{current}} of {{total}}",
      allowClose: true, // Esc / overlay-click / × all exit
      overlayOpacity: 0.6,
      stagePadding: 6,
      stageRadius: 8,
      smoothScroll: true,
      // Block clicks on the spotlighted element so the tour can't accidentally
      // trigger real actions (e.g. Save/Submit on the question page).
      disableActiveInteraction: true,
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Done",
      steps: driveSteps,
      // Replace driver's index-based progress with our running ordinal.
      onPopoverRender: (popover) => {
        if (popover.progress) popover.progress.innerText = `Step ${shown} of ${total}`;
      },
      // Manual control: we navigate + wait, then move ourselves.
      onNextClick: () => advance(1),
      onPrevClick: () => advance(-1),
      onCloseClick: () => driverRef.current?.destroy(),
      onDestroyed: () => {
        recordSeen();
        driverRef.current = null;
        // Return the user to where they started the tour — but NOT when the
        // teardown was caused by the provider unmounting (logout / leaving the
        // authenticated shell), where navigating back would be wrong.
        if (
          !unmountingRef.current &&
          userRef.current &&
          window.location.pathname !== startPath
        ) {
          navRef.current(startPath);
        }
      },
    });

    driverRef.current = d;

    // Kick off at the first presentable step (welcome is always presentable).
    void showFrom(0, 1);
    }); // end Promise.all(...).then
  }, []);

  const isActive = useCallback(() => driverRef.current?.isActive() ?? false, []);

  return (
    <TourContext.Provider value={{ startTour, isActive }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextShape {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside <TourProvider>");
  return ctx;
}
